import { SetFormActionWithId } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { buildNestedPatch, getNestedValue } from '@app/finxone-web-frontend/app/lib/utils/nested-object-path.utils';
import { Store } from '@ngxs/store';

export type DirectorRecord = Record<string, unknown>;

/**
 * Reads the current directors array stored under rootFormID's FormActionState, at
 * childKey - typically "members" (a flat top-level key), but can be any dot-path if a project
 * needs the directors array nested elsewhere. Populated live as the add/edit wizard's Form
 * Widgets (formType: 'array') write into it, one array index at a time.
 */
export function getDirectorsFromState(
  store: Store,
  rootFormID: string,
  childKey: string,
): DirectorRecord[] {
  const state = store.selectSnapshot(FormActionState.getFormActionStateWithId(rootFormID));
  const directors = getNestedValue<DirectorRecord[]>(state?.formData, childKey);
  return Array.isArray(directors) ? directors : [];
}

/**
 * A director is "complete" once every field in requiredFields is present and non-empty.
 * Used to render e.g. "Information complete" / "Information required" per row.
 */
export function isDirectorComplete(director: DirectorRecord | undefined, requiredFields: string[]): boolean {
  if (!director || requiredFields.length === 0) return false;
  return requiredFields.every((field) => {
    const value = director[field];
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null && value !== '';
  });
}

/**
 * Removes the director at `index` and persists the updated array. Does not touch the
 * array-edit-index (there is no wizard session active for a plain remove).
 */
export function removeDirectorAt(store: Store, rootFormID: string, childKey: string, index: number): void {
  const currentFormData = store.selectSnapshot(
    FormActionState.getFormActionStateWithId(rootFormID),
  )?.formData;
  const directors = getNestedValue<DirectorRecord[]>(currentFormData, childKey) ?? [];
  const updated = directors.filter((_: unknown, i: number) => i !== index);
  store.dispatch(
    new SetFormActionWithId({ type: '', formData: buildNestedPatch(currentFormData, childKey, updated) }, rootFormID),
  );
}

/**
 * Pins "append a new entry" as the array-edit-index for childKey, so the next Form Widget
 * (formType: 'array') to resolve this path allocates and pins the next available slot itself.
 * Call before navigating to the first page of the add-director wizard.
 */
export function startAddDirector(
  workflowFormsService: WorkflowFormsService,
  rootFormID: string,
  childKey: string,
): void {
  workflowFormsService.setActiveFormArrayIndex(`${rootFormID}.${childKey}`, null);
}

/**
 * Pins `index` as the array-edit-index for childKey, so every Form Widget on the wizard's
 * pages binds to that specific director's slot. Call before navigating to the first page
 * of the edit-director wizard.
 */
export function startEditDirector(
  workflowFormsService: WorkflowFormsService,
  rootFormID: string,
  childKey: string,
  index: number,
): void {
  workflowFormsService.setActiveFormArrayIndex(`${rootFormID}.${childKey}`, index);
}
